import { apolloClientHolder, AppContainer, DummyToRememberPeopleToCast, EntityTablePage, FieldDescriptor, Optional } from "@crispico/foundation-react";
import { FilterOperators } from "@crispico/foundation-gwt-js";
import _ from "lodash";
import gql from "graphql-tag";
import React, { ReactElement } from "react";
import { Button, Icon } from "semantic-ui-react";
import { ScriptableUiHighlightWrapper, WithHW } from "@famiprog-foundation/scriptable-ui";
import { ScriptableUiFieldEditor } from "@crispico/foundation-react/entity_crud/fieldEditors/FieldEditor";
import { XopsAppContainerContextValue } from "XopsAppContainerContext";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { AssociationEditorProps, AssociationExtraProps, AssociationFieldEditor, AssociationFieldEditorProps, AssociationFieldEditorState } from "@crispico/foundation-react/entity_crud/fieldEditors/AssociationFieldEditor";
import { SelectExtOption } from "@crispico/foundation-react/components/selectExt/SelectExt";
import { FieldEditorProps } from "@crispico/foundation-react/entity_crud/fieldRenderersEditors";
import { components, ControlProps } from "react-select";
import { FormikProps } from "formik";
import { SortEnd } from "react-sortable-hoc";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { FieldInterval } from "@crispico/foundation-react/entity_crud/CrudSettings";
import { missionEntityDescriptor, taskEntityDescriptor } from "AppEntityDescriptors";
import { MissionStatus } from "pages/gantt/typeRenderers/constants";
import { MissionStatusGroup } from "pages/gantt/AssignTasksToResourcePage";
import { entityDescriptors, ID } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { UnassignButtonRRC } from "./UnassignButton";
import { AssignTasksToResourceAction } from "pages/Task/actions/AssignTasksToResourceAction";

export class Mission_MissionTypeFieldDescriptor extends FieldDescriptor {

    protected renderFieldEditorInternal(EditorClass: any, props: FieldEditorProps): ReactElement {
        const { tempSettingsXops } = (AppContainer.INSTANCE.getAppContainerContextValue() as XopsAppContainerContextValue).initializationsForClient;

        return React.createElement(EditorClass, {
            ...props, onChangeValue: (value: any) => {
                // when change the type also change the status according settings
                if (value) {
                    const status = tempSettingsXops.tempMissionTypeSettings.find((mts) => mts.missionType === value.id)?.initialStatus;
                    status && props.formikProps!.setFieldValue("status", status);
                }
            }
        });
    }
}

export class Mission_EquipmentTypeFieldDescriptor extends FieldDescriptor {

    protected renderFieldEditorInternal(EditorClass: any, props: FieldEditorProps): ReactElement {

        return React.createElement(EditorClass, {
            ...props, onChangeValue: (value: any) => {
                // when change the equipment type, remove the equipment resource if it is not the same type as the selected one
                const er = props.formikProps!.values['equipmentResource'];
                if (er) {
                    if (!value || value.id != er.equipmentType?.id) {
                        props.formikProps!.setFieldValue("equipmentResource", null);
                    }
                    props.formikProps!.setFieldValue("vehicleAttachment", null);
                }
            }
        });
    }
}

export class Mission_EquipmentResourceFieldDescriptor extends FieldDescriptor {

    protected renderFieldEditorInternal(_: any, props: FieldEditorProps): ReactElement {
        const equipmentType = props.formikProps!.values['equipmentType'];
        const availableFilter = Filter.create("available", FilterOperators.forBoolean.equals, "true");

        return React.createElement(Mission_EquipmentResourceFieldEditor as any, {
            ...props, onChangeValue: (value: any) => {
                // when change the equipment resource, set the equipment type from the selected value
                if (value) {
                    props.formikProps!.setFieldValue("equipmentType", value.equipmentType);
                }
            },
            filter: equipmentType ? Filter.createComposed(FilterOperators.forComposedFilter.and, [availableFilter, Filter.create("equipmentType.id", FilterOperators.forNumber.equals, equipmentType.id)]) : availableFilter,
            additionalSearchFields: ['equipmentType']
        });
    }
}

export class Mission_HumanResourceFieldDescriptor extends FieldDescriptor {

    protected renderFieldEditorInternal(_: any, props: FieldEditorProps): ReactElement {

        return React.createElement(Mission_HumanResourceFieldEditor as any, {
            ...props, onChangeValue: (value: any) => {
                // when change human resource, set the equipment resource from the selected value if it does not exist
                const er = props.formikProps!.values['equipmentResource'];
                if (value && !er) {
                    props.formikProps!.setFieldValue("equipmentResource", value.vehicle);
                }
            },
            filter: Filter.create("available", FilterOperators.forBoolean.equals, "true"),
            additionalSearchFields: ['vehicle']
        });
    }
}

export class Mission_StartEndTimeFieldDescriptor extends FieldDescriptor {

    protected renderFieldEditorInternal(EditorClass: any, props: FieldEditorProps): ReactElement {

        return React.createElement(EditorClass, {
            ...props, onChangeValue: (value: any) => {
                const oldValue = props.formikProps!.values[this.name];
                if (value && new Date(value).getTime() != new Date(oldValue).getTime()) {
                    props.formikProps!.setFieldValue(this.updateFieldName, oldValue);
                    props.formikProps!.setFieldValue(this.name + "Manual", true);
                }
            }
        });
    }
}

class Mission_HumanResourceFieldEditor extends AssociationFieldEditor<FieldEditorProps & AssociationEditorProps & AssociationExtraProps> {

    protected getLabel(entity: any): string {
        return this.getEntityDescriptor().toMiniString(entity);
    }
}

class Mission_EquipmentResourceFieldEditor extends Mission_HumanResourceFieldEditor {
}

type Mission2_TasksFieldEditorProps = FieldEditorProps & AssociationFieldEditorProps & AssociationExtraProps;

export class Mission2_TasksFieldEditor extends AssociationFieldEditor<Mission2_TasksFieldEditorProps, AssociationFieldEditorState> {

    assignTaskActionRef = React.createRef<AssignTasksToResourceAction>();

    constructor(props: Mission2_TasksFieldEditorProps) {
        super(props);
        this.afterAssignUnassign = this.afterAssignUnassign.bind(this);
        this.onClickAssign = this.onClickAssign.bind(this);
    }

    protected renderExtraButtons({ children, ...props }: ControlProps<SelectExtOption>) {
        return (<components.Control {...props}>
            {children}
        </components.Control>);
    };

    protected onSortEnd(sort: SortEnd) {
        // need a copy because the state is read-only
        let selectedOption = _.cloneDeep(this.state.selectedOption);
        const movedElement = selectedOption.splice(sort.oldIndex, 1)[0];
        selectedOption.splice(sort.newIndex, 0, movedElement);
        this.changeSelectedValue(selectedOption);
        this.setState({ selectedOption });
    }

    protected getSelectExtProps() {
        return {
            ...super.getSelectExtProps(),
            hideMenu: true,
            isSearchable: false,
            className: "Mission2_tasks_field wh100",
            customRendererForMultiValueRemove: () => null
        };
    }

    protected async loadTasks(id: number) {
        const loadOperationName = `${_.lowerFirst(missionEntityDescriptor.name)}Service_findById`;
        const loadQuery = gql(`query q($id: Long) { 
                    ${loadOperationName}(id: $id) {
                        ${ID} tasks { ${ID} ${taskEntityDescriptor.getGraphQlFieldsToRequest(taskEntityDescriptor.miniFields)} }
                    }
                }`);
        const tasks = (await apolloClientHolder.apolloClient.query({ query: loadQuery, variables: { id } })).data[loadOperationName]?.tasks ?? [];
        this.props.formikProps.setFieldValue("tasks", tasks);
    }

    protected async afterAssignUnassign() {
        await this.loadTasks(this.props.formikProps.values.id);
        entityDescriptors[missionEntityDescriptor.name].entityTablePage.current?.refresh();
    }

    protected onClickAssign(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();// see EntityEditorFormSimple.renderFormikChild
        this.assignTaskActionRef.current!.setState({
            assignTasksToResourcePageProps: {
                missionId: this.props.formikProps.values?.id,
                equipmentResourceId: this.props.formikProps.values?.equipmentResource?.id,
                humanResourceId: this.props.formikProps.values?.humanResource?.id,
                defaultMissionStatusGroup: MissionStatusGroup.EXISTING,
                onAssign: this.afterAssignUnassign,
            }
        });
    }

    protected isDisableAssignUnassign() {
        return !this.props.formikProps.values.id || this.props.formikProps.values.status == MissionStatus.MISSION_STATUS_FINISHED
    }

    protected renderEditorComponent(s: WithHW<ScriptableUiFieldEditor.Main>, hw: ScriptableUiHighlightWrapper) {
        const orderOfFlightsLockedFD = missionEntityDescriptor.getField("orderOfFlightsLocked");
        const fi: Optional<FieldInterval> = orderOfFlightsLockedFD.getFieldInterval(orderOfFlightsLockedFD.getFieldValue(this.props.formikProps.values));
        const color = fi && fi.color ? AppMetaTempGlobals.appMetaInstance.getColor(fi.color) : undefined;

        return <div className="flex-container gap10">
            <div className="gap10 flex-container-row justify-content-space-between">
                <div className="flex-grow">{super.renderEditorComponent(s, hw)}</div>
                <div className="flex-container flex-justify-content-center gap5">
                    <Button size='mini' color="green" disabled={this.isDisableAssignUnassign()} onClick={this.onClickAssign}>
                        <>
                            <Icon name='add' />
                            {_msg("TasksFieldEditor.assign.label")}
                        </>
                    </Button>
                    <UnassignButtonRRC id="unassignButtonRRC" disabled={this.isDisableAssignUnassign()} tasks={this.props.formikProps.values?.tasks || []} onUnassign={this.afterAssignUnassign} />
                    <AssignTasksToResourceAction ref={this.assignTaskActionRef} getEntityUid={() => ""} getEntityAt={() => ({ id: null })} tablePage={entityDescriptors[missionEntityDescriptor.name].entityTablePage.current!} />
                </div>
            </div>
            <div className="flex-container-row gap10">
                <label style={{ color: color }}>{orderOfFlightsLockedFD.getIcon()}{orderOfFlightsLockedFD.getLabel(true)}</label>
                {orderOfFlightsLockedFD.renderFieldEditor(this.props.formikProps)}
            </div>
        </div>
    }
}

export class Mission2_TasksFieldDescriptor extends FieldDescriptor {
    renderFieldEditor(formikProps: FormikProps<any>, _?: DummyToRememberPeopleToCast) {
        return <Mission2_TasksFieldEditor isClearable queryLimit={-1} placeholder=""
            fieldDescriptor={this} formikProps={formikProps} isMulti />;
    }
}
